"use client";

import React, { useEffect, useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { MousePointerClick, Users, ShieldCheck, Bug, SprayCan, ArrowLeft, Zap, Activity, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const servicesData = [{
  id: "tab-0",
  title: "مكافحة النمل الأبيض",
  fullTitle: "إبادة النمل الأبيض (الأرضة)",
  desc: "حلول جذرية للتخلص من مستعمرات النمل الأبيض وحماية أساسات المباني باستخدام أقوى المبيدات المعتمدة.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661713/img/termite_control_treatment_premium.png",
  icon: Bug,
  stats: "ضمان 15 سنة",
  features: ["حقن التربة", "حواجز كيميائية", "فحص دوري"]
}, {
  id: "tab-1",
  title: "رش الدفان",
  fullTitle: "معالجة التربة قبل البناء",
  desc: "تأمين المباني الجديدة ضد الحشرات من خلال رش التربة قبل صبة النظافة لضمان حماية طويلة الأمد.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661713/img/soil_treatment_construction_premium.png",
  icon: SprayCan,
  stats: "حماية معتمدة",
  features: ["مبيدات طويلة الأمد", "شهادة ضمان", "تنفيذ احترافي"]
}, {
  id: "tab-2",
  title: "إبادة الصراصير",
  fullTitle: "مكافحة الصراصير والآفات",
  desc: "القضاء التام على الصراصير والآفات المنزلية باستخدام طعوم ومبيدات آمنة تماماً على الصحة العامة.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661713/img/cockroach_pest_elimination_premium.png",
  icon: Activity,
  stats: "إبادة فورية",
  features: ["طعوم ألمانية", "بدون رائحة", "آمن للأطفال"]
}, {
  id: "tab-3",
  title: "مكافحة القوارض",
  fullTitle: "صيد وإبادة الفئران",
  desc: "أنظمة متطورة لتتبع وصيد القوارض ومنع دخولها للمنشآت التجارية والسكنية بفعالية تقنية عالية.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661713/img/rodent_control_traps_premium.png",
  icon: ShieldCheck,
  stats: "نتائج مضمونة",
  features: ["محطات طعوم", "سد الثغرات", "متابعة مستمرة"]
}, {
  id: "tab-4",
  title: "التعقيم الشامل",
  fullTitle: "خدمات التطهير والتعقيم",
  desc: "تعقيم المنشآت ضد الفيروسات والبكتيريا باستخدام مواد معتمدة دولياً لبيئة صحية وآمنة ومستدامة.",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661713/img/sanitization_disinfection_service_premium.png",
  icon: Users,
  stats: "أمان صحي",
  features: ["تعقيم نانو", "مواد معتمدة", "حماية 24/7"]
}];
export default function DigitalGrowthEngine() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="228258" id="حلولنا-المتخصصة" className="bg-[linear-gradient(to bottom right,var(--primary),rgba(var(--rgb-accent),0.05),var(--secondary))] text-white p-4 sm:p-6 md:p-8 relative overflow-hidden font-primary" dir="rtl">
            {}
            <div className="absolute top-0 right-0 w-[80%] sm:w-[70%] lg:w-[600px] h-[80%] sm:h-[70%] lg:h-[600px] bg-[rgba(var(--rgb-accent),0.05)] rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[80%] sm:w-[70%] lg:w-[600px] h-[80%] sm:h-[70%] lg:h-[600px] bg-[rgba(var(--rgb-secondary),0.20)] rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.02] pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10">
                <div ref={headerRef} className={cn("max-w-4xl mb-16 transition-all duration-700", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[rgba(var(--rgb-accent),0.10)] text-[var(--accent)] text-sm font-bold mb-6 border border-accent/20 backdrop-blur-md">
                        <Zap className="size-4" />
                        <span className="uppercase tracking-widest">التميز التقني في المكافحة</span>
                    </div>
                    <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
                        نحن نمنحك <span className="text-accent">بيئة محصنة</span>، <br />
                        بأحدث معايير الجودة العالمية
                    </h2>
                    <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
                        في سرايا، ندمج الخبرة الميدانية مع التقنيات الحديثة لنقدم لك حلولاً جذرية تتجاوز مجرد الرش التقليدي، مع ضمانات حقيقية تصل إلى 15 عاماً.
                    </p>
                </div>

                <div ref={contentRef} className={cn("transition-all duration-700 delay-200", isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
                    <Tabs defaultValue={servicesData[0].id} className="flex flex-col lg:flex-row gap-8 items-stretch">
                        {}
                        <TabsList className="flex-col w-full sm:w-full lg:w-[350px] h-auto bg-transparent p-0 gap-3 shrink-0 justify-start">
                            {servicesData.map((service, i) => <TabsTrigger key={service.id} value={service.id} data-index={i} className="w-full group relative flex items-center gap-4 text-right p-4 h-auto rounded-xl border border-white/5 bg-[rgba(var(--rgb-secondary),0.40)] backdrop-blur-md hover:bg-secondary/60 hover:border-accent/30 data-[state=active]:bg-secondary data-[state=active]:border-accent/50 data-[state=active]:shadow-lg transition-all duration-300 overflow-hidden">
                                    <div className="p-2.5 rounded-lg bg-[rgba(var(--rgb-primary),0.50)] border border-white/10 text-[rgba(var(--rgb-white),0.40)] group-data-[state=active]:text-accent group-data-[state=active]:border-accent/30 transition-all group-hover:scale-110 group-hover:shadow-2xl shrink-0">
                                        <service.icon className="size-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-bold text-white group-data-[state=active]:text-accent transition-colors">
                                            {service.title}
                                        </h3>
                                        <p className="text-xs text-white/50 group-data-[state=active]:text-white/80 transition-colors line-clamp-1">
                                            {service.stats}
                                        </p>
                                    </div>
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[var(--accent)] scale-y-0 group-data-[state=active]:scale-y-100 transition-transform duration-300 origin-top" />
                                </TabsTrigger>)}
                        </TabsList>

                        {}
                        <div className="flex-1 min-h-[300px] sm:min-h-[400px] lg:min-h-[550px] relative rounded-xl overflow-hidden border border-white/10 bg-secondary/30 backdrop-blur-xl shadow-2xl transition-all duration-300">
                            {servicesData.map((service, i) => <TabsContent key={service.id} value={service.id} data-index={i} className="absolute inset-0 m-0 p-0 data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in data-[state=active]:slide-in-from-bottom-4 duration-500">
                                    <div className="relative w-full h-full flex flex-col lg:flex-row">
                                        {}
                                        <div className="relative w-full lg:w-1/2 h-64 lg:h-auto overflow-hidden">
                                            <img src={service.img} data-wvc-srcset={`${service.img}-wvc-srcset`} data-wvc-sizes={`${service.img}-wvc-sizes`} alt={service.fullTitle} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80 mix-blend-luminosity hover:mix-blend-normal transition-all duration-1000 scale-105 hover:scale-110 hover:shadow-2xl" />
                                            <div className="absolute inset-0 bg-gradient-to-l from-[rgba(var(--rgb-secondary),0.30)] via-transparent to-transparent hidden lg:block" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(var(--rgb-secondary),0.30)] via-transparent to-transparent lg:hidden" />
                                        </div>

                                        {}
                                        <div className="flex-1 p-8 lg:p-12 flex flex-col justify-center">
                                            <div className="flex items-center gap-3 mb-6">
                                                <span className="px-3 py-1 rounded-full bg-[rgba(var(--rgb-accent),0.20)] text-[var(--accent)] text-xs font-bold border border-accent/30">
                                                    {service.stats}
                                                </span>
                                                <div className="h-px flex-1 bg-white/10" />
                                            </div>
                                            
                                            <h4 className="text-3xl font-bold text-white mb-4">{service.fullTitle}</h4>
                                            <p className="text-white/70 leading-relaxed mb-8 text-lg">
                                                {service.desc} نعتمد على بروتوكولات عالمية تضمن القضاء التام على الآفات مع الحفاظ على أعلى معايير السلامة البيئية.
                                            </p>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                                                {service.features.map((feature, idx) => <div key={idx} className="flex items-center gap-3 text-white/80">
                                                        <CircleCheck className="size-5 text-accent shrink-0" />
                                                        <span className="text-sm font-medium">{feature}</span>
                                                    </div>)}
                                            </div>

                                            <div className="flex flex-wrap gap-4 mt-auto">
                                                <Button asChild variant="outline" className="rounded-full px-10 py-6 border-white/20 text-white hover:bg-[rgba(var(--rgb-white),0.10)] font-bold text-lg transition-all duration-300">
                                                    <Link to="#">التفاصيل الفنية</Link>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </TabsContent>)}
                        </div>
                    </Tabs>
                </div>
            </div>
        </section>;
}